/* eslint-disable sonarjs/no-duplicate-string */
import { parse, stringify } from "querystring";

import { useState } from "react";

import { Box, Grid, Stack, Theme, useTheme } from "@mui/material";
import { DonationFormCategory, PaymentMethod, TicketingFormCategory } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useSnackbar } from "notistack";

import { AmplitudeEvents } from "../../../constants/amplitude";
import { useAmplitude } from "../../../hooks/amplitude/useAmplitude";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import Image from "../../Image/Image";
import { ZeffyLogoRedirect } from "../../ZeffyLogoRedirect/ZeffyLogoRedirect";
import { ThankYouPageQueryParams } from "../type";

import { getEmailText } from "./helper";

import { FormDivider } from "@/components/forms-design-system/FormDivider";
import { FormTypography } from "@/components/forms-design-system/FormTypography";
import { DashboardButton } from "@/dashboard-design-system/Button";
import { DashboardDialog } from "@/dashboard-design-system/Dialog/DashboardDialog";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useTranslate } from "@/hooks/useTranslate";

const MAIN_CONTENT_WIDTH = 686;
const WIDTH_FULL = "100%";
export const ImpactChainUtmSourceName = "impact_chain_source"; // remove utm from name to avoid creating a custom question
export const OldImpactChainUtmSourceName = "utm_impact_chain_source";

export const ImpactChainThanksYouPageContent = ({
  poweredByColor,
}: {
  poweredByColor: string;
  shareCircleColor: string;
}) => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const theme = useTheme<Theme>();
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const { isPhoneScreen } = useMediaQuery();
  const query = Object.fromEntries(searchParams.entries());

  const {
    organizationName,
    email,
    formType,
    shareLink,
    generateTaxReceipt,
    generateETicket,
    formCategory,
    paymentMethod,
  } = query as unknown as ThankYouPageQueryParams;
  const paymentMethodIsAchOrPad = paymentMethod === PaymentMethod.Ach || paymentMethod === PaymentMethod.Pad;
  const isAuction = formCategory === TicketingFormCategory.Auction;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pathname, queryParams] = shareLink?.split("?") ?? ["", ""];
  const link = `${pathname}?${stringify({ ...parse(queryParams), [ImpactChainUtmSourceName]: email })}`;
  const isDonationOrCampaignForm =
    formCategory === DonationFormCategory.DonationForm || formCategory === DonationFormCategory.PeerToPeerCampaign;

  const messages = ((): { message1: string; message2: string } => {
    if (isDonationOrCampaignForm) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageDonation"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageDonation2"),
      };
    }
    if (formCategory === TicketingFormCategory.Event || formCategory === TicketingFormCategory.Lottery) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageEvent"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageEvent2"),
      };
    }
    if (formCategory === TicketingFormCategory.Shop || formCategory === TicketingFormCategory.Custom) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageShop"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageShop2"),
      };
    }
    if (formCategory === TicketingFormCategory.MembershipV2) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageMembership"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageMembership2"),
      };
    }
    if (formCategory === TicketingFormCategory.Auction) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageAuction"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageAuction2"),
      };
    }
    if (formCategory === TicketingFormCategory.Lottery) {
      return {
        message1: t("dashboard", "thankYouPage.dialog.shareMessageEvent"),
        message2: t("dashboard", "thankYouPage.dialog.shareMessageEvent2"),
      };
    }
    return { message1: "", message2: "" };
  })();

  const ShareMessage = () => {
    return (
      <>
        <DashboardTypography variant="body2" vibe="text-brand-intense" sx={{ mb: 1 }} component="span">
          {messages.message1}
        </DashboardTypography>
        <DashboardTypography variant="subtitle2" vibe="text-brand-intense" sx={{ mb: 1 }} component="span">
          {organizationName}
        </DashboardTypography>
        <DashboardTypography variant="body2" vibe="text-brand-intense" sx={{ mb: 1 }} component="span">
          {messages.message2}
        </DashboardTypography>
        <br />
        <br />
        <DashboardTypography variant="body2" vibe="text-brand-intense" sx={{ mb: 1 }} component="span">
          {t("dashboard", "thankYouPage.dialog.shareMessage3")}
        </DashboardTypography>
      </>
    );
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      logAmplitudeEvent(AmplitudeEvents.ImpactChainSharingCompleted, { method: "direct link" });
      navigator.clipboard.writeText(link);
      enqueueSnackbar(t("dashboard", "thankYouPage.dialog.copied"), { vibe: "positive" });
    }
  };

  const handleCopyFullMessage = () => {
    if (navigator.clipboard) {
      logAmplitudeEvent(AmplitudeEvents.ImpactChainSharingCompleted, { method: "canned message" });
      navigator.clipboard.writeText(
        `${messages.message1}${organizationName}${messages.message2}\n\n${t("dashboard", "thankYouPage.dialog.shareMessage3")}${link}`
      );
      enqueueSnackbar(t("dashboard", "thankYouPage.dialog.copied"), { vibe: "positive" });
    }
  };

  const emailText = getEmailText({
    paymentMethodIsAchOrPad,
    email,
    formType,
    t,
    isAuction,
    generateTaxReceipt,
    generateETicket,
  });

  if (isPhoneScreen) {
    return (
      <>
        {isModalOpen && (
          <DashboardDrawer
            onClose={() => setIsModalOpen(false)}
            open={isModalOpen}
            anchor="bottom"
            height="90vh"
            noDivider
            noPadding
          >
            <Grid container direction="column" style={{ height: "inherit" }}>
              <Grid item xs width="100%">
                <Stack sx={{ paddingRight: 1, paddingLeft: 1 }}>
                  <Grid container justifyContent="center" paddingBottom={2}>
                    <Image src="/images/impact_chain_2.svg" alt="Impact Chain" width={194} height={75} />
                  </Grid>
                  <DashboardTypography variant="h6" vibe="text-neutral-intense">
                    {t("dashboard", "thankYouPage.shareOnSocialNetworks")}
                  </DashboardTypography>
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate" mt={1}>
                    {t("dashboard", "thankYouPage.shareToCharities")}
                  </DashboardTypography>
                  <Box
                    sx={{
                      mt: 3,
                      mb: 2,
                      bgcolor: theme.palette.surface.brand.quiet,
                      borderRadius: 1,
                      p: 2,
                      position: "relative",
                    }}
                  >
                    <Image
                      src="/assets/icons/quotation-mark.svg"
                      alt="quotation-mark"
                      width={26}
                      height={22}
                      sx={{ position: "absolute", top: -12, l: 2 }}
                    />
                    <ShareMessage />

                    <DashboardLink href={link} target="_blank">
                      <DashboardTypography
                        variant="subtitle2"
                        vibe="text-brand-moderate"
                        sx={{ mb: 1, overflowWrap: "break-word" }}
                        component="span"
                      >
                        {link}
                      </DashboardTypography>
                    </DashboardLink>
                  </Box>
                </Stack>
              </Grid>
              <Grid item width="100%">
                <Stack gap={1}>
                  <DashboardButton variant="filled" fullWidth onClick={handleCopyFullMessage}>
                    {t("dashboard", "thankYouPage.dialog.copyMessage")}
                  </DashboardButton>
                  <DashboardButton variant="outlined" fullWidth onClick={handleCopyLink}>
                    {t("dashboard", "thankYouPage.dialog.copyLinkOnly")}
                  </DashboardButton>
                </Stack>
              </Grid>
            </Grid>
          </DashboardDrawer>
        )}
        <Box
          sx={{
            marginTop: 12,
            mx: "auto",
            bgcolor: "transparent",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          {/* Title and Subtitle */}
          <Stack spacing={1} alignItems="center" width={WIDTH_FULL}>
            <FormTypography
              variant="h4"
              vibe="intense"
              sx={{ textAlign: "center", whiteSpace: "pre-line" }}
              component="span"
            >
              {t("dashboard", "thankYouPage.head.title")}.{" "}
              <FormTypography
                variant="h4"
                vibe="brand"
                sx={{ textAlign: "center", whiteSpace: "pre-line" }}
                component="span"
              >
                {t("dashboard", "thankYouPage.title.youAreAmazing")}
              </FormTypography>
            </FormTypography>

            <FormTypography
              variant="body2"
              vibe="moderate"
              sx={{ textAlign: "center", whiteSpace: "pre-line" }}
              component="span"
            >
              {emailText}{" "}
              {!paymentMethodIsAchOrPad && (
                <Box component="span" color={theme.palette.forms.text.brand}>
                  {email}
                </Box>
              )}
            </FormTypography>
          </Stack>

          {/* Divider and Powered by */}
          <Grid container sx={{ margin: theme.spacing(3, 0) }}>
            <Grid item sx={{ display: "flex", width: "100%", alignItems: "center" }}>
              <FormDivider sx={{ flexGrow: 1, marginRight: 2 }} />
              <Grid item sx={{ marginRight: 1 }}>
                <FormTypography variant="body2" vibe="moderate" color={poweredByColor}>
                  {t("common", "thankYouPage.poweredBy")}
                </FormTypography>
              </Grid>
              <Grid item>
                <ZeffyLogoRedirect color={poweredByColor} width={62} height={24} noPadding />
              </Grid>
              <FormDivider sx={{ flexGrow: 1, marginLeft: 2 }} />
            </Grid>
          </Grid>

          {/* Card: Make Your Gift Go Further */}
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1,
              width: WIDTH_FULL,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              padding: theme.spacing(1, 0, 2, 2),
            }}
          >
            <Box>
              <Grid container sx={{ display: "flex", alignItems: "center" }}>
                <Grid item xs sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <DashboardTypography variant="subtitle1" vibe="text-neutral-intense" sx={{ mb: 1 }}>
                    {t("dashboard", "thankYouPage.shareOnSocialNetworks")}
                  </DashboardTypography>
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ mb: 2 }}>
                    {t("dashboard", "thankYouPage.shareToCharities")}
                  </DashboardTypography>
                  <Grid container>
                    <DashboardButton
                      variant="filled"
                      size="medium"
                      onClick={() => {
                        setIsModalOpen(true);
                        logAmplitudeEvent(AmplitudeEvents.ThankYouPageSharingClicked, { impact_chain: true });
                      }}
                    >
                      {t("dashboard", "thankyouPage.shareThis")}
                    </DashboardButton>
                  </Grid>
                </Grid>
                <Grid item sx={{ marginLeft: 2 }}>
                  <Image src="/images/impact_chain.svg" alt="impact chain" width={81} height={114} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      {isModalOpen && (
        <DashboardDialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          hideIcon
          sx={{ "& .MuiDialogContent-root": { pt: 0 } }}
          maxWidth="md"
        >
          <Box sx={{ px: 9, pb: 1, width: 654, maxWidth: "100%" }}>
            <Stack alignItems="center" width="100%" sx={{ paddingRight: 1, paddingLeft: 1 }}>
              <Stack>
                <Image src="/images/impact_chain_2.svg" alt="Balance Boost" width={252} height={110} />
              </Stack>
              <DashboardTypography variant="h4" vibe="text-neutral-intense" sx={{ textAlign: "center" }}>
                {t("dashboard", "thankYouPage.shareOnSocialNetworks")}
              </DashboardTypography>
              <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ textAlign: "center" }} mt={1}>
                {t("dashboard", "thankYouPage.shareToCharities")}
              </DashboardTypography>
              <Box
                sx={{
                  mt: 3,
                  bgcolor: theme.palette.surface.brand.quiet,
                  borderRadius: 1,
                  width: "100%",
                  p: 2,
                  mb: 2,
                  position: "relative",
                }}
              >
                <Image
                  src="/assets/icons/quotation-mark.svg"
                  alt="quotation-mark"
                  width={26}
                  height={22}
                  sx={{ position: "absolute", top: -12, l: 2 }}
                />
                <ShareMessage />

                <DashboardLink href={link} target="_blank">
                  <DashboardTypography
                    variant="subtitle2"
                    vibe="text-brand-moderate"
                    sx={{ mb: 1, overflowWrap: "break-word" }}
                    component="span"
                  >
                    {link}
                  </DashboardTypography>
                </DashboardLink>
              </Box>
              <Stack direction="row" spacing={2} width="100%" justifyContent="center" mt={2}>
                <DashboardButton variant="outlined" size="medium" onClick={handleCopyLink}>
                  {t("dashboard", "thankYouPage.dialog.copyLinkOnly")}
                </DashboardButton>
                <DashboardButton variant="filled" size="medium" onClick={handleCopyFullMessage}>
                  {t("dashboard", "thankYouPage.dialog.copyMessage")}
                </DashboardButton>
              </Stack>
            </Stack>
          </Box>
        </DashboardDialog>
      )}
      <Box
        sx={{
          width: { xs: WIDTH_FULL, md: MAIN_CONTENT_WIDTH },
          mx: "auto",
          bgcolor: "transparent",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Title and Subtitle */}
        <Stack spacing={2} alignItems="center" width={WIDTH_FULL} sx={{ marginBottom: 2 }}>
          <FormTypography variant="h2" vibe="intense" sx={{ textAlign: "center", whiteSpace: "pre-line" }}>
            {t("dashboard", "thankYouPage.head.title")}
          </FormTypography>
          <FormTypography variant="h2" vibe="brand" sx={{ textAlign: "center", whiteSpace: "pre-line" }}>
            {t("dashboard", "thankYouPage.title.youAreAmazing")}
          </FormTypography>
          <FormTypography variant="body1" vibe="moderate" component="span">
            {t("dashboard", "thankyouPage.donation.emailDisclaimer.taxReceipt")}
            <FormTypography variant="body1" vibe="brand" component="span">
              {email}
            </FormTypography>
          </FormTypography>
        </Stack>

        {/* Divider and Powered by */}
        <Grid container sx={{ margin: theme.spacing(3, 0) }}>
          <Grid item sx={{ display: "flex", width: "100%", alignItems: "center" }}>
            <FormDivider sx={{ flexGrow: 1, marginRight: 2 }} />
            <Grid item sx={{ marginRight: 1 }}>
              <FormTypography variant="body2" vibe="moderate" color={poweredByColor}>
                {t("common", "thankYouPage.poweredBy")}
              </FormTypography>
            </Grid>
            <Grid item>
              <ZeffyLogoRedirect color={poweredByColor} width={113} height={34} noPadding />
            </Grid>
            <FormDivider sx={{ flexGrow: 1, marginLeft: 2 }} />
          </Grid>
        </Grid>

        {/* Card: Make Your Gift Go Further */}
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 1,
            width: WIDTH_FULL,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            padding: theme.spacing(2, 0, 2, 4),
            marginTop: 6,
          }}
        >
          <Box sx={{ flex: 1, height: 185 }}>
            <Grid container sx={{ height: "100%" }}>
              <Grid item xs sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <DashboardTypography variant="h4" vibe="text-neutral-intense" sx={{ mb: 1 }}>
                  {t("dashboard", "thankYouPage.shareOnSocialNetworks")}
                </DashboardTypography>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate" sx={{ mb: 2 }}>
                  {t("dashboard", "thankYouPage.shareToCharities")}
                </DashboardTypography>
                <Grid container>
                  <DashboardButton
                    variant="filled"
                    size="medium"
                    onClick={() => {
                      setIsModalOpen(true);
                      logAmplitudeEvent(AmplitudeEvents.ThankYouPageSharingClicked, { impact_chain: true });
                    }}
                  >
                    {t("dashboard", "thankyouPage.shareThis")}
                  </DashboardButton>
                </Grid>
              </Grid>
              <Grid item sx={{ marginLeft: 5 }}>
                <Image src="/images/impact_chain.svg" alt="impact chain" width={144} height={185} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};
